import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type ComplianceTrend = 'improving' | 'declining' | 'stable';

export interface ComplianceScore {
  overallScore: number;
  workoutCompliance: number;
  nutritionCompliance: number;
  trend: ComplianceTrend;
  periodDays: number;
  scheduledSessions: number;
  completedSessions: number;
  daysWithMeals: number;
  totalDays: number;
}

interface UseComplianceScoreOptions {
  periodDays?: number;
}

export function useComplianceScore(
  clientId: string | undefined,
  options: UseComplianceScoreOptions = {}
) {
  const { periodDays = 7 } = options;
  const [score, setScore] = useState<ComplianceScore | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadScore = useCallback(async () => {
    if (!clientId) {
      setScore(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const now = new Date();
      const startDate = new Date();
      startDate.setDate(now.getDate() - periodDays);
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = now.toISOString().split('T')[0];

      // Calculate previous period for trend
      const prevStartDate = new Date();
      prevStartDate.setDate(startDate.getDate() - periodDays);
      const prevStartDateStr = prevStartDate.toISOString().split('T')[0];

      // Fetch all data in parallel
      const [
        currentSessionsResult,
        prevSessionsResult,
        currentNutritionResult,
        prevNutritionResult,
      ] = await Promise.all([
        // Current period sessions
        supabase
          .from('sessions')
          .select('id, date, cancelled, completed_at')
          .eq('client_id', clientId)
          .gte('date', startDateStr)
          .lte('date', endDateStr),
        // Previous period sessions (for trend)
        supabase
          .from('sessions')
          .select('id, cancelled, completed_at')
          .eq('client_id', clientId)
          .gte('date', prevStartDateStr)
          .lt('date', startDateStr),
        // Current period nutrition
        supabase
          .from('nutrition_daily_summaries')
          .select('summary_date, meal_count')
          .eq('client_id', clientId)
          .gte('summary_date', startDateStr)
          .lte('summary_date', endDateStr),
        // Previous period nutrition
        supabase
          .from('nutrition_daily_summaries')
          .select('summary_date, meal_count')
          .eq('client_id', clientId)
          .gte('summary_date', prevStartDateStr)
          .lt('summary_date', startDateStr),
      ]);

      // Calculate current workout compliance
      const currentSessions = currentSessionsResult.data || [];
      const scheduledSessions = currentSessions.filter(s => !s.cancelled).length;
      const completedSessions = currentSessions.filter(
        s => !s.cancelled && s.completed_at !== null
      ).length;
      const workoutCompliance = scheduledSessions > 0
        ? Math.round((completedSessions / scheduledSessions) * 100)
        : 100; // No sessions scheduled = 100% compliance

      // Calculate current nutrition compliance
      const currentNutrition = currentNutritionResult.data || [];
      const daysWithMeals = currentNutrition.filter(
        d => d.meal_count !== null && d.meal_count > 0
      ).length;
      const totalDays = periodDays;
      const nutritionCompliance = Math.round((daysWithMeals / totalDays) * 100);

      // Calculate overall score (weighted average: 50% workout, 50% nutrition)
      const overallScore = Math.round(
        (workoutCompliance * 0.5) + (nutritionCompliance * 0.5)
      );

      // Calculate previous period scores for trend
      const prevSessions = prevSessionsResult.data || [];
      const prevScheduled = prevSessions.filter(s => !s.cancelled).length;
      const prevCompleted = prevSessions.filter(
        s => !s.cancelled && s.completed_at !== null
      ).length;
      const prevWorkoutCompliance = prevScheduled > 0
        ? Math.round((prevCompleted / prevScheduled) * 100)
        : 100;

      const prevNutrition = prevNutritionResult.data || [];
      const prevDaysWithMeals = prevNutrition.filter(
        d => d.meal_count !== null && d.meal_count > 0
      ).length;
      const prevNutritionCompliance = Math.round((prevDaysWithMeals / periodDays) * 100);

      const prevOverallScore = Math.round(
        (prevWorkoutCompliance * 0.5) + (prevNutritionCompliance * 0.5)
      );

      // Determine trend
      let trend: ComplianceTrend = 'stable';
      const scoreDiff = overallScore - prevOverallScore;
      if (scoreDiff >= 10) {
        trend = 'improving';
      } else if (scoreDiff <= -10) {
        trend = 'declining';
      }

      setScore({
        overallScore,
        workoutCompliance,
        nutritionCompliance,
        trend,
        periodDays,
        scheduledSessions,
        completedSessions,
        daysWithMeals,
        totalDays,
      });
    } catch (err) {
      console.error('Error loading compliance score:', err);
      setError('Failed to load compliance score');
    } finally {
      setLoading(false);
    }
  }, [clientId, periodDays]);

  useEffect(() => {
    loadScore();
  }, [loadScore]);

  return { score, loading, error, reload: loadScore };
}
